import { useEffect, useMemo, useRef, useState } from "react";
import {
  MdClose,
  MdPrint,
  MdRefresh,
  MdComputer,
  MdExpandMore,
  MdCheckCircle,
  MdErrorOutline,
} from "react-icons/md";

import axios from "../axiosConfig";
import { BASE_SERVER_URL } from "../sampleData";
import "../label-print-modal.css";

/* ------------------------------------------------------------------ *
 * Label preview modal — replaces LabelPrintPrompt in pages/Print.js.
 *
 * Same backend contract as before:
 *   GET  /api/printers            -> { success, printers: [{id, name, computer}] }
 *   GET  /api/printers/defaults   -> { success, defaults: { label_print: { printerId } } }
 *   POST /generate-pdf/:type      -> { success, url }   (type: funeral | retail | card)
 *   POST /print/label             -> { success, message } with { pdfUrls, printerId }
 *
 * `detail` is the label-details object the print page derives from the
 * work order (latePerson, funeral_date, tributes, recipient, phoneNumber,
 * location, card_message …). Edits are pushed back through `onChange`
 * so they survive closing and reopening the modal.
 * ------------------------------------------------------------------ */

/* One stable random id per browser so the server can keep each user's
   generated PDFs apart — same key the legacy page used. */
function userPrintIdentifier() {
  const key = "RF-print-identifier";
  try {
    let id = localStorage.getItem(key);
    if (!id) {
      id = Math.floor(1000000000 + Math.random() * 9000000000).toString();
      localStorage.setItem(key, id);
    }
    return id;
  } catch (e) {
    return "0000000000";
  }
}

/* The labels this order produces: one per tribute for funerals, an
   address label plus a card for retail. `cue` is the selection key. */
function labelsOf(detail) {
  if (detail.type === "Funeral") {
    return (detail.tributes || []).map((t, i) => ({
      cue: i,
      type: "funeral",
      title: `Tribute ${i + 1}`,
      caption: [t.code, t.product_name || t.product_type].filter(Boolean).join(" · "),
      payload: {
        latePerson: detail.latePerson,
        funeralDate: detail.funeral_date || "N/A",
        productCode: t.code,
        sashPrice: t.sashPrice || 0,
        sashMessage: t.sash_message || 0,
        productPrice: t.customPrice ? t.customPrice : t.price,
        comment: t.comment,
        number: i,
      },
    }));
  }
  return [
    { cue: "retail", type: "retail", title: "Address label", caption: detail.recipient || "", payload: detail },
    { cue: "card", type: "card", title: "Card", caption: "Card message", payload: detail },
  ];
}

/* Viewer open-params: hide the toolbar and sidebar and fit the whole page,
   so the small preview shows the label itself rather than PDF controls. */
const PREVIEW_PARAMS = "#toolbar=0&navpanes=0&view=Fit";

function LabelCard({ label, url, state, checked, onToggle }) {
  return (
    <div className={"lpm-card " + (checked ? "is-on" : "")}>
      <label className="lpm-card-head">
        <input type="checkbox" checked={checked} onChange={onToggle} disabled={!url} />
        <span className="lpm-card-title">{label.title}</span>
        {label.caption && <span className="lpm-card-cap">{label.caption}</span>}
      </label>
      <div className="lpm-card-body">
        {state === "error" ? (
          <div className="lpm-card-state is-error">
            <MdErrorOutline />
            Could not generate this label
          </div>
        ) : !url ? (
          <div className="lpm-card-state">
            <span className="lpm-skel" />
            Generating label…
          </div>
        ) : (
          <iframe title={label.title} src={url + PREVIEW_PARAMS} className="lpm-pdf" />
        )}
      </div>
    </div>
  );
}

export default function LabelPrintModal({ detail, onChange, onClose, subtitle }) {
  const funeral = detail.type === "Funeral";
  const labels = useMemo(() => labelsOf(detail), [detail]);

  /* per-label generated PDF url and state, keyed by cue */
  const [pdfs, setPdfs] = useState({});
  const [states, setStates] = useState({});
  const [selected, setSelected] = useState(() => labels.map((l) => l.cue));
  const [generation, setGeneration] = useState(0);

  const [printers, setPrinters] = useState([]);
  const [printer, setPrinter] = useState(null);
  const [printersLoading, setPrintersLoading] = useState(true);
  const [pickerOpen, setPickerOpen] = useState(false);
  const pickerRef = useRef(null);

  const [sending, setSending] = useState(false);
  const [notice, setNotice] = useState(null); // { tone: "ok" | "err", text }

  /* draft edits — applied on "Regenerate" like the legacy prompt */
  const [draft, setDraft] = useState({
    latePerson: detail.latePerson || "",
    recipient: detail.recipient || "",
    phoneNumber: detail.phoneNumber || "",
    location: detail.location || "",
    card_message: detail.card_message || "",
  });
  const dirty = funeral
    ? draft.latePerson !== (detail.latePerson || "")
    : draft.recipient !== (detail.recipient || "") ||
      draft.phoneNumber !== (detail.phoneNumber || "") ||
      draft.location !== (detail.location || "") ||
      draft.card_message !== (detail.card_message || "");

  /* printers + default */
  useEffect(() => {
    let alive = true;
    setPrintersLoading(true);
    axios
      .get(`${BASE_SERVER_URL}/api/printers`)
      .then((res) => {
        if (!alive || !res.data.success) return [];
        const list = res.data.printers || [];
        setPrinters(list);
        return axios
          .get(`${BASE_SERVER_URL}/api/printers/defaults`)
          .then((def) => {
            const id = def.data.success && def.data.defaults && def.data.defaults.label_print
              ? def.data.defaults.label_print.printerId
              : null;
            const hit = list.find((p) => p.id === id);
            if (alive) setPrinter(hit || list[0] || null);
          })
          .catch(() => {
            if (alive) setPrinter(list[0] || null);
          });
      })
      .catch(() => {})
      .finally(() => {
        if (alive) setPrintersLoading(false);
      });
    return () => {
      alive = false;
    };
  }, []);

  /* generate every label; re-runs when the details change */
  useEffect(() => {
    let alive = true;
    setPdfs({});
    setStates({});
    labels.forEach((l) => {
      axios
        .post(`${BASE_SERVER_URL}/generate-pdf/${l.type}`, {
          ...l.payload,
          userIdentifier: userPrintIdentifier(),
        })
        .then((res) => {
          if (!alive) return;
          if (res.data.success) {
            setPdfs((prev) => ({ ...prev, [l.cue]: `${BASE_SERVER_URL}/${res.data.url}` }));
            setStates((prev) => ({ ...prev, [l.cue]: "ready" }));
          } else setStates((prev) => ({ ...prev, [l.cue]: "error" }));
        })
        .catch(() => {
          if (alive) setStates((prev) => ({ ...prev, [l.cue]: "error" }));
        });
    });
    return () => {
      alive = false;
    };
  }, [labels, generation]);

  /* close on Escape; picker closes on outside click */
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    const onDown = (e) => {
      if (pickerRef.current && !pickerRef.current.contains(e.target)) setPickerOpen(false);
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onDown);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onDown);
    };
  }, [onClose]);

  const readyUrls = selected.map((cue) => pdfs[cue]).filter(Boolean);
  const allOn = labels.length > 0 && selected.length === labels.length;
  const canPrint = readyUrls.length > 0 && Boolean(printer) && !sending;

  function toggle(cue) {
    setSelected((prev) => (prev.includes(cue) ? prev.filter((c) => c !== cue) : [...prev, cue]));
  }

  function regenerate() {
    if (dirty) {
      onChange(
        funeral
          ? { latePerson: draft.latePerson }
          : {
              recipient: draft.recipient,
              phoneNumber: draft.phoneNumber,
              location: draft.location,
              card_message: draft.card_message,
            }
      );
      /* detail change re-runs generation through `labels` */
    } else setGeneration((g) => g + 1);
    setNotice(null);
  }

  function print() {
    if (!canPrint) return;
    setSending(true);
    setNotice(null);
    axios
      .post(`${BASE_SERVER_URL}/print/label`, { pdfUrls: readyUrls, printerId: printer.id })
      .then((res) => {
        setNotice(
          res.data.success
            ? { tone: "ok", text: `Sent ${readyUrls.length} label${readyUrls.length === 1 ? "" : "s"} to ${printer.name}.` }
            : { tone: "err", text: res.data.message || "The print job was not accepted." }
        );
      })
      .catch(() => setNotice({ tone: "err", text: "Could not reach the print service." }))
      .finally(() => setSending(false));
  }

  const noLabels = funeral && labels.length === 0;

  return (
    <div className="lpm-backdrop" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className={"lpm " + (noLabels ? "is-narrow" : "")} role="dialog" aria-modal="true" aria-labelledby="lpm-title">
        <header className="lpm-head">
          <div className="lpm-head-text">
            <span className="lpm-kicker">{funeral ? "Funeral" : "Retail"} labels</span>
            <h2 id="lpm-title" className="lpm-title">Label preview</h2>
            {subtitle && <span className="lpm-sub">{subtitle}</span>}
          </div>
          <button type="button" className="lpm-x" onClick={onClose} aria-label="Close">
            <MdClose />
          </button>
        </header>

        {noLabels ? (
          <div className="lpm-empty">
            <MdErrorOutline />
            <strong>No tributes on this order.</strong>
            <span>Funeral labels are generated per tribute. Add at least one tribute product to print labels.</span>
            <button type="button" className="wop-btn is-ghost" onClick={onClose}>Close</button>
          </div>
        ) : (
          <>
            <div className="lpm-body">
              <aside className="lpm-edit">
                <span className="lpm-edit-h">Label details</span>
                {funeral ? (
                  <label className="lpm-field">
                    <span>Late person's name</span>
                    <input
                      value={draft.latePerson}
                      onChange={(e) => setDraft({ ...draft, latePerson: e.target.value })}
                    />
                  </label>
                ) : (
                  <>
                    <label className="lpm-field">
                      <span>Recipient</span>
                      <input value={draft.recipient} onChange={(e) => setDraft({ ...draft, recipient: e.target.value })} />
                    </label>
                    <label className="lpm-field">
                      <span>Phone</span>
                      <input value={draft.phoneNumber} onChange={(e) => setDraft({ ...draft, phoneNumber: e.target.value })} />
                    </label>
                    <label className="lpm-field">
                      <span>Location</span>
                      <input value={draft.location} onChange={(e) => setDraft({ ...draft, location: e.target.value })} />
                    </label>
                    <label className="lpm-field">
                      <span>Card message</span>
                      <textarea
                        rows={4}
                        value={draft.card_message}
                        onChange={(e) => setDraft({ ...draft, card_message: e.target.value })}
                      />
                    </label>
                  </>
                )}
                <button type="button" className={"wop-btn " + (dirty ? "is-primary" : "is-ghost")} onClick={regenerate}>
                  <MdRefresh /> {dirty ? "Apply & regenerate" : "Regenerate labels"}
                </button>
                <span className="lpm-edit-hint">
                  Edits only change the printed labels, not the work order.
                </span>
              </aside>

              <section className="lpm-labels">
                <div className="lpm-labels-bar">
                  <label className="lpm-all">
                    <input
                      type="checkbox"
                      checked={allOn}
                      onChange={() => setSelected(allOn ? [] : labels.map((l) => l.cue))}
                    />
                    Select all
                  </label>
                  <span className="lpm-count">
                    {selected.length} of {labels.length} selected
                  </span>
                </div>
                <div className="lpm-grid">
                  {labels.map((l) => (
                    <LabelCard
                      key={l.cue}
                      label={l}
                      url={pdfs[l.cue]}
                      state={states[l.cue]}
                      checked={selected.includes(l.cue)}
                      onToggle={() => toggle(l.cue)}
                    />
                  ))}
                </div>
              </section>
            </div>

            <footer className="lpm-foot">
              {notice && (
                <span className={"lpm-notice is-" + notice.tone}>
                  {notice.tone === "ok" ? <MdCheckCircle /> : <MdErrorOutline />}
                  {notice.text}
                </span>
              )}
              <div className="lpm-foot-right">
                <div className="lpm-picker" ref={pickerRef}>
                  <button
                    type="button"
                    className="lpm-picker-btn"
                    onClick={() => setPickerOpen((o) => !o)}
                    disabled={printersLoading || printers.length === 0}
                    aria-haspopup="listbox"
                    aria-expanded={pickerOpen}
                  >
                    <span className="lpm-picker-k">Print on</span>
                    <span className="lpm-picker-v">
                      {printersLoading ? (
                        "Finding printers…"
                      ) : printer ? (
                        <>
                          <MdPrint /> {printer.name}
                          {printer.computer && printer.computer.name && (
                            <span className="lpm-picker-pc">
                              <MdComputer /> {printer.computer.name}
                            </span>
                          )}
                        </>
                      ) : (
                        "No printers online"
                      )}
                    </span>
                    <MdExpandMore className="lpm-picker-caret" />
                  </button>
                  {pickerOpen && (
                    <ul className="lpm-picker-list" role="listbox">
                      {printers.map((p) => (
                        <li
                          key={p.id}
                          role="option"
                          aria-selected={printer && printer.id === p.id}
                          className={printer && printer.id === p.id ? "is-on" : ""}
                          onClick={() => {
                            setPrinter(p);
                            setPickerOpen(false);
                          }}
                        >
                          <MdPrint />
                          <span className="lpm-picker-name">
                            {p.name}
                            {p.computer && p.computer.name && (
                              <span className="lpm-picker-pc">
                                <MdComputer /> {p.computer.name}
                              </span>
                            )}
                          </span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
                <button type="button" className="wop-btn is-ghost" onClick={onClose}>
                  Close
                </button>
                <button type="button" className="wop-btn is-primary" onClick={print} disabled={!canPrint}>
                  <MdPrint /> {sending ? "Sending…" : `Print ${readyUrls.length || ""} label${readyUrls.length === 1 ? "" : "s"}`}
                </button>
              </div>
            </footer>
          </>
        )}
      </div>
    </div>
  );
}
